import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import StickyFooter from '../components/StickyFooter';
import { submitCardApplication } from "../utils/api";
import {
  Box,
  Paper,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Button,
  TextField,
} from '@mui/material';
import Header from '../components/Header';

const cardOptions = [
  {
    value: 'visa',
    label: 'Visa',
    description: 'Accepted worldwide, suitable for most online purchases',
    image: 'https://upload.wikimedia.org/wikipedia/commons/4/41/Visa_Logo.png',
  },
  {
    value: 'mastercard',
    label: 'Mastercard',
    description: 'Global acceptance with enhanced security features',
    image: 'https://upload.wikimedia.org/wikipedia/commons/0/04/Mastercard-logo.png',
  },
  {
    value: 'amex',
    label: 'American Express',
    description: 'Premium benefits and exclusive rewards program',
    image: 'https://upload.wikimedia.org/wikipedia/commons/3/30/American_Express_logo.svg',
  },
];

const ApplyVirtualCard = () => {
  const [selectedCard, setSelectedCard] = useState('');
  const [cardLevel, setCardLevel] = useState('');
  const [form, setForm] = useState({
    nameOnCard: '',
    dob: '',
    address: '',
    city: '',
    postalCode: '',
    country: '',
    phone: '',
    currency: '',
    cardType: 'virtual',
  });

  // ✅ FIX: add this
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        card_brand: selectedCard,
        card_level: cardLevel,
        card_type: form.cardType,
        name_on_card: form.nameOnCard,
        dob: form.dob,
        phone: form.phone,
        address: form.address,
        city: form.city,
        postal_code: form.postalCode,
        country: form.country,
        currency: form.currency,
      };

      const response = await submitCardApplication(payload);

      alert("✅ Card application submitted successfully!");
      console.log(response.data);

      // reset form
      setSelectedCard('');
      setCardLevel('');
      setForm({
        nameOnCard: '',
        dob: '',
        address: '',
        city: '',
        postalCode: '',
        country: '',
        phone: '',
        currency: '',
        cardType: 'virtual',
      });
    } catch (err) {
      console.error(err);
      alert("❌ Failed to submit application");
    }
  };
  
  return (
    <Box p={{ xs: 2, md: 4 }}>
      <Header />

      {/* Breadcrumb */}
      <Typography variant="body2" mt="60px" fontSize="12px" color="text.secondary" mb={1}>
        Dashboard &gt; Cards &gt; Apply
      </Typography>

      {/* Title + Back Button */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" fontWeight={600} sx={{ mr: '15%' }}>
          Apply for Virtual Card
        </Typography>
        <Button
          variant="outlined"
          size="small"
          component={RouterLink}
          to="/cards"
          sx={{ fontSize: '0.8rem', textAlign: 'center', textTransform: 'none' }}
        >
          &larr; Back to Cards
        </Button>
      </Box>

      {/* Info Box */}
      <Paper
        elevation={3}
        sx={{
          borderRadius: 3,
          p: 2,
          background: 'linear-gradient(to right, #0ea5e9, #2F80ED)',
          color: '#fff',
          mb: 2,
        }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          Apply for a Virtual Card
        </Typography>
        <Typography fontSize="0.85rem">
          Get instant access to a virtual card for online payments and subscriptions
        </Typography>
      </Paper>

      {/* Card Form */}
      <Paper variant="outlined" sx={{ borderRadius: 3, p: 3, mb: 10 }}>
        <form onSubmit={handleSubmit}>
          <Typography variant="subtitle2" fontWeight={600} mb={2}>
            Card Details
          </Typography>

          {/* Card Choice */}
          <FormControl component="fieldset" fullWidth>
            <RadioGroup value={selectedCard} onChange={(e) => setSelectedCard(e.target.value)}>
              {cardOptions.map((card) => (
                <FormControlLabel
                  key={card.value}
                  value={card.value}
                  control={<Radio />}
                  label={
                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                      <Box>
                        <Typography fontWeight={600}>{card.label}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {card.description}
                        </Typography>
                      </Box>
                      <img src={card.image} alt={card.label} width={40} />
                    </Box>
                  }
                  sx={{
                    mb: 2,
                    border: '1px solid #e2e8f0',
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                    '&:hover': { borderColor: '#2F80ED' },
                  }}
                />
              ))}
            </RadioGroup>
          </FormControl>

          {/* Card Level */}
          <Box mt={3}>
            <FormControl fullWidth required>
              <InputLabel>Select a card level</InputLabel>
              <Select
                value={cardLevel}
                label="Select a card level"
                onChange={(e) => setCardLevel(e.target.value)}
              >
                <MenuItem value="basic">Basic</MenuItem>
                <MenuItem value="premium">Premium</MenuItem>
                <MenuItem value="platinum">Platinum</MenuItem>
              </Select>
              <Typography variant="caption" color="text.secondary" mt={1}>
                Different levels offer varied spending limits and features
              </Typography>
            </FormControl>
          </Box>

          {/* Required Form Fields */}
          <Box mt={3} display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2}>
            <TextField
              label="Name on Card"
              name="nameOnCard"
              value={form.nameOnCard}
              onChange={handleChange}
              required
            />
            <TextField
              label="Date of Birth"
              name="dob"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={form.dob}
              onChange={handleChange}
              required
            />
            <TextField
              label="Billing Address"
              name="address"
              value={form.address}
              onChange={handleChange}
              required
            />
            <TextField
              label="City"
              name="city"
              value={form.city}
              onChange={handleChange}
              required
            />
            <TextField
              label="Postal Code"
              name="postalCode"
              value={form.postalCode}
              onChange={handleChange}
              required
            />
            <TextField
              label="Country"
              name="country"
              value={form.country}
              onChange={handleChange}
              required
            />
            <TextField
              label="Phone Number"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              required
            />
            <FormControl fullWidth required>
              <InputLabel>Currency</InputLabel>
              <Select name="currency" value={form.currency} onChange={handleChange}>
                <MenuItem value="USD">USD</MenuItem>
                <MenuItem value="EUR">EUR</MenuItem>
                <MenuItem value="GBP">GBP</MenuItem>
                <MenuItem value="NGN">NGN</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Card Type */}
          <Box mt={3}>
            <FormControl component="fieldset">
              <Typography variant="subtitle2" mb={1}>Card Type</Typography>
              <RadioGroup
                row
                name="cardType"
                value={form.cardType}
                onChange={handleChange}
              >
                <FormControlLabel value="virtual" control={<Radio />} label="Virtual" />
                <FormControlLabel value="physical" control={<Radio />} label="Physical" />
              </RadioGroup>
            </FormControl>
          </Box>

          {/* Submit Button */}
          <Box mt={4} textAlign="right">
            <Button type="submit" variant="contained" color="primary">
              Submit Application
            </Button>
          </Box>
        </form>
      </Paper>
      <StickyFooter />
    </Box>
  );
};

export default ApplyVirtualCard;